import {
  Badge,
  Box,
  Button,
  Checkbox,
  LazyImage,
  Tooltip,
  Typography,
} from "@bca/ui-library";
import { BadgeProps } from "@bca/ui-library/dist/Badge/Badge";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { position } from "styled-system";
import { CheckboxProps } from "@bca/ui-library/dist/Checkbox/checkbox.type";

import { Vehicle } from "../Interfaces/vehicle.type";
import buildLinkUrl from "../APICalls/buildLinkUrl";
import getData from "../APICalls/getData";
import { paymentStatusMapper } from "../Mappers/vehicleDetailsMappers";
import DownloadSVG from "../Icons/DownloadSVG";
import downloadImagesDataPush from "../AdobeDataPushes/vehicleCard/downloadImagesDataPush";
import ClickableBox from "../StyledComponents/ClickableBox";
import PositionedBox from "../StyledComponents/PositionedBox";
import GradientBox from "../StyledComponents/GradientBox";
import selectForMultipleDelivery from "../Utilities/selectForMultipleDelivery";
import { checkIsEligibleForDelivery } from "../Utilities/checkIsEligibleForDelivery";
import { SHOW_SNACKBAR } from "../redux/slices/snackbarSlice";
import selectForMultipleDeliveryDataPush from "../AdobeDataPushes/vehicleCard/selectForMultipleDeliveryDataPush";
import { useAuthZeroContext } from "../context/AuthZeroContext";

interface ImageContainerProps {
  vehicle: Vehicle;
  isChecked: boolean;
  degradedServices: string[];
}

interface CheckboxWithOnClickProps extends CheckboxProps {
  onClick: (e: Event) => void;
}

const CheckboxWithOnClick = styled(Checkbox)<CheckboxWithOnClickProps>`
  ${position};
`;

const ImageContainer: React.FunctionComponent<ImageContainerProps> = ({
  vehicle,
  isChecked,
  degradedServices = [],
}) => {
  const dispatch = useDispatch();
  const selectForMultipleDeliveryHandler = () => {
    isChecked
      ? selectForMultipleDeliveryDataPush(vehicle, "checkbox", "remove", "card")
      : selectForMultipleDeliveryDataPush(
          vehicle,
          "checkbox",
          "select",
          "card"
        );
    selectForMultipleDelivery(vehicle, dispatch);
    dispatch(SHOW_SNACKBAR(false));
  };

  const invoiceServiceDegraded = degradedServices?.includes("InvoiceService");
  const deliveryServiceDegraded = degradedServices?.includes("DeliveryService");
  const mediaServerServiceDegraded =
    degradedServices?.includes("MediaServerService");

  const shouldShowPaymentStatusBadge =
    vehicle.paymentStatus &&
    !invoiceServiceDegraded &&
    !deliveryServiceDegraded;

  const shouldShowDownloadImages =
    vehicle.hasImages && !mediaServerServiceDegraded;

  const shouldShowCheckbox =
    checkIsEligibleForDelivery(vehicle) && !deliveryServiceDegraded;

  const getBadgeAndCheckboxJustifyContent = () => {
    if (shouldShowPaymentStatusBadge && shouldShowCheckbox) {
      return "space-between";
    } else if (shouldShowPaymentStatusBadge) {
      return "flex-start";
    }
    return "flex-end";
  };

  const getTooltipText = (isChecked: boolean) =>
    isChecked ? "Deselect for Delivery" : "Select for Delivery";

  const {
    data: { isToUseAuthZero, authZeroAccessToken },
  } = useAuthZeroContext();

  const downloadImages = async () => {
    let getDownloadImagesUrl;
    if (vehicle.hasImages) {
      downloadImagesDataPush(vehicle);
      getDownloadImagesUrl = buildLinkUrl(
        vehicle.generateImagesZipFileUrl,
        isToUseAuthZero
      );
      const downloadImagesUrl = await getData(
        getDownloadImagesUrl,
        authZeroAccessToken,
        isToUseAuthZero
      );
      window.open(downloadImagesUrl?.href, "_ blank");
    }
  };

  return (
    <ClickableBox
      position="relative"
      width="100%"
      height="224px"
      clickable={vehicle.hasImages}
      onClick={downloadImages}
    >
      <PositionedBox
        position="absolute"
        width="100%"
        height="100%"
        bottom="0"
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
      >
        <LazyImage
          width="100%"
          height="100%"
          src={vehicle.masterImageUrl}
          alt="vehicle-image"
          css={`
            position: absolute;
            bottom: 0;
          `}
        ></LazyImage>
        <GradientBox
          width="100%"
          height="100%"
          position="absolute"
          css={`
            bottom: 0;
          `}
        />
        <Box
          display="flex"
          justifyContent={getBadgeAndCheckboxJustifyContent()}
          marginLeft={4}
          marginRight={1}
          marginTop={4}
        >
          {shouldShowPaymentStatusBadge && (
            <Badge
              variant={
                paymentStatusMapper[vehicle.paymentStatus]
                  ?.variant as BadgeProps["variant"]
              }
              label={paymentStatusMapper[vehicle.paymentStatus]?.label}
              width="fit-content"
              zIndex={10}
            ></Badge>
          )}
          {shouldShowCheckbox && (
            <Tooltip
              id={vehicle.vrm}
              customTrigger={
                <CheckboxWithOnClick
                  id={vehicle.vrm}
                  onChange={selectForMultipleDeliveryHandler}
                  checked={isChecked}
                  onClick={(e) => e.stopPropagation()}
                />
              }
              placement="bottom"
            >
              <Typography variant="body-small" color="footer-blue" margin={0}>
                {getTooltipText(isChecked)}
              </Typography>
            </Tooltip>
          )}
        </Box>
        <PositionedBox position="absolute" bottom={16} left={20}>
          {shouldShowDownloadImages && (
            <Button
              variant="secondary-white"
              height="32px"
              width="fit-content"
              fontWeight="normal"
              padding={0}
              css="border: none;"
            >
              <DownloadSVG
                fill="white"
                width="18px"
                height="18px"
                mr="15px"
              ></DownloadSVG>
              Download Images
            </Button>
          )}
        </PositionedBox>
      </PositionedBox>
    </ClickableBox>
  );
};

export default ImageContainer;
